import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence } from 'framer-motion';
import { Camera, Scan, Sparkles, Wand2, MonitorPlay, UserSquare2, ArrowRight, CheckCircle2, SplitSquareHorizontal, Brain, Share2 } from 'lucide-react';
import LiquidLightToggle from './LiquidLightToggle';

const WorkflowSection = () => {
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            id: '01',
            category: 'STEP 01',
            title: 'The Foundation.',
            subtitle: 'Capture',
            features: [
                { icon: <Scan className="w-5 h-5" />, title: 'Guided Capture', desc: 'Detailed in-app guidance for perfect clinical photography.' },
                { icon: <Brain className="w-5 h-5" />, title: 'AI Analysis', desc: 'Instant mapping of facial landmarks, midline, and smile arc.' },
                { icon: <CheckCircle2 className="w-5 h-5" />, title: 'Automated Alignment', desc: 'Precision alignment for consistent, predictable results.' }
            ],
            colors: {
                text: 'text-cyan-400',
                border: 'border-cyan-500/50',
                bg: 'bg-cyan-950/30',
                gradient: 'from-cyan-500/10 to-blue-500/10',
                glow: 'shadow-[0_0_50px_-12px_rgba(6,182,212,0.2)]',
                lineGradient: 'from-cyan-500 to-transparent',
                hoverBorder: 'group-hover:border-cyan-500/50',
                iconBg: 'bg-cyan-500/10'
            },
            Content: () => (
                <div className="relative w-full h-full">
                    <motion.div
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="w-full h-full overflow-hidden"
                    >
                        <img
                            src="/images/workflow/step1.jpg"
                            alt="Step 1: Capture"
                            className="w-full h-full object-cover"
                        />
                        {/* Premium overlay gradient for text legibility if needed, mostly for aesthetics */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
                    </motion.div>
                </div>
            )
        },
        {
            id: '02',
            category: 'STEP 02',
            title: 'The Vision.',
            subtitle: 'Design',
            features: [
                { icon: <Wand2 className="w-5 h-5" />, title: 'Select Modality', desc: 'Cosmetic, Orthodontic, or Restorative workflows.' },
                { icon: <MonitorPlay className="w-5 h-5" />, title: 'Automated Planning', desc: 'AI-generated proposals tailored to facial harmony.' },
                { icon: <Sparkles className="w-5 h-5" />, title: 'Precision Refinement', desc: 'Fine-tune surgical details with expert control.' }
            ],
            colors: {
                text: 'text-purple-400',
                border: 'border-purple-500/50',
                bg: 'bg-purple-950/30',
                gradient: 'from-purple-500/10 to-pink-500/10',
                glow: 'shadow-[0_0_50px_-12px_rgba(168,85,247,0.2)]',
                lineGradient: 'from-purple-500 to-transparent',
                hoverBorder: 'group-hover:border-purple-500/50',
                iconBg: 'bg-purple-500/10'
            },
            Content: () => (
                <div className="relative w-full h-full bg-black">
                    {/* Liquid Light takes full container */}
                    <LiquidLightToggle />
                    <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/10 rounded-3xl" />
                </div>
            )
        },
        {
            id: '03',
            category: 'STEP 03',
            title: 'The Emotion.',
            subtitle: 'Reveal',
            features: [
                { icon: <SplitSquareHorizontal className="w-5 h-5" />, title: 'Interactive Comparison', desc: 'Immersive before/after sliders to engage patients.' },
                { icon: <MonitorPlay className="w-5 h-5" />, title: 'Dynamic Animation', desc: 'Showcase the transformation with our signature morph.' },
                { icon: <Share2 className="w-5 h-5" />, title: 'Shareable Reports', desc: 'Export high-quality visuals for patient take-home.' }
            ],
            colors: {
                text: 'text-amber-400',
                border: 'border-amber-500/50',
                bg: 'bg-amber-950/30',
                gradient: 'from-amber-500/10 to-orange-500/10',
                glow: 'shadow-[0_0_50px_-12px_rgba(245,158,11,0.2)]',
                lineGradient: 'from-amber-500 to-transparent',
                hoverBorder: 'group-hover:border-amber-500/50',
                iconBg: 'bg-amber-500/10'
            },
            Content: () => {
                const [imgError, setImgError] = useState<string | null>(null);

                return (
                    // FULL BLEED CONTAINER
                    <div className="relative w-full h-full cursor-crosshair group">

                        {/* Error Message Display */}
                        {imgError && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center bg-red-900/90 text-white p-4 text-center font-bold">
                                IMAGE ERROR: {imgError}
                            </div>
                        )}

                        {/* Base Layer: AFTER Image (The Result) */}
                        <div className="absolute inset-0 z-10">
                            <img
                                src="/images/workflow/step3-after.jpg?v=2"
                                alt="After Result"
                                className="w-full h-full object-cover"
                                onError={(e) => setImgError(`After Image Failed: ${e.currentTarget.src}`)}
                            />
                            {/* Floating Label */}
                            <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full text-[10px] font-bold text-white tracking-[0.2em] border border-white/10 shadow-sm opacity-60 group-hover:opacity-100 transition-opacity">
                                AFTER
                            </div>
                        </div>

                        {/* Top Layer: BEFORE Image (The Original) - Cross-Fade Animation */}
                        <motion.div
                            className="absolute inset-0 z-20"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: [1, 1, 0, 0, 1] }}
                            transition={{ duration: 6, ease: "easeInOut", repeat: Infinity, repeatDelay: 1 }}
                        >
                            <img
                                src="/images/workflow/step3-before.jpg?v=2"
                                alt="Before Original"
                                className="w-full h-full object-cover"
                                onError={(e) => setImgError(`Before Image Failed: ${e.currentTarget.src}`)}
                            />
                            {/* Floating Label */}
                            <div className="absolute top-6 left-6 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full text-[10px] font-bold text-white tracking-[0.2em] border border-white/10 shadow-sm opacity-60 group-hover:opacity-100 transition-opacity">
                                BEFORE
                            </div>
                        </motion.div>

                        {/* Progress Bar / Time Indicator - Bottom aligned */}
                        <motion.div
                            className="absolute bottom-0 left-0 h-1 bg-amber-500 z-30"
                            initial={{ width: "0%" }}
                            animate={{ width: ["0%", "100%", "0%"] }}
                            transition={{ duration: 6, ease: "linear", repeat: Infinity, repeatDelay: 1 }}
                        />

                        {/* Bottom Badge - Elegant & Minimal */}
                        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-40 pointer-events-none">
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="bg-black/60 backdrop-blur-xl px-5 py-2.5 rounded-full border border-white/10 flex items-center gap-3 shadow-2xl"
                            >
                                <div className="relative flex items-center justify-center">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse absolute" />
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-ping opacity-75 absolute" />
                                </div>
                                <span className="text-xs font-bold text-white/90 tracking-widest uppercase">AI Transformation</span>
                            </motion.div>
                        </div>
                    </div>
                );
            }
        }
    ];

    return (
        <section className="relative bg-transparent py-24 lg:py-40"> {/* INCREASED PADDING from py-32 to py-40 */}
            {/* Background Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Section Header */}
                <div className="text-center mb-24 lg:mb-32">
                    {/* Minimal Badge - Adjusted backdrop */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/40 backdrop-blur-md border border-white/[0.08] mb-8"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_6px_rgba(45,212,191,0.6)]" />
                        <span className="text-xs font-medium tracking-wide text-zinc-400">Streamlined Workflow</span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 tracking-tight"
                        style={{
                            background: 'linear-gradient(180deg, #ffffff 0%, #a1a1aa 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Capture. Design. Reveal.
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-lg md:text-xl text-zinc-500 max-w-xl mx-auto"
                    >
                        A frictionless workflow designed for the busy operatory.
                    </motion.p>
                </div>

                <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
                    {/* Left Column: Scrolling Text */}
                    <div className="w-full lg:w-1/2 order-2 lg:order-1 pt-0 lg:pt-12">
                        {steps.map((step, index) => (
                            <Step
                                key={step.id}
                                step={step}
                                index={index}
                                onActive={() => setActiveStep(index)}
                            />
                        ))}
                    </div>

                    {/* Right Column: Sticky Visual */}
                    <div className="hidden lg:block w-full lg:w-1/2 relative order-1 lg:order-2">
                        <div className="sticky top-32 h-[75vh] max-h-[800px] w-full flex items-center justify-center perspective-1000">

                            {/* Unified Card Frame */}
                            <motion.div
                                className={`relative w-full h-full bg-zinc-950 rounded-[2.5rem] border border-white/10 overflow-hidden shadow-2xl transition-all duration-700 ${steps[activeStep].colors.glow}`}
                                layout
                            >
                                {/* Subtle Inner Glow/Gradeint specific to step */}
                                <motion.div
                                    className={`absolute inset-0 bg-gradient-to-br ${steps[activeStep].colors.gradient} opacity-20`}
                                    animate={{ opacity: [0.15, 0.3, 0.15] }}
                                    transition={{ duration: 5, repeat: Infinity }}
                                />

                                {/* Glass Reflection Effect */}
                                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-50 pointer-events-none z-20" />

                                {/* Content Transition */}
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeStep}
                                        initial={{ opacity: 0, scale: 1.02 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.6, ease: "circOut" }}
                                        className="absolute inset-0 z-10"
                                    >
                                        {React.createElement(steps[activeStep].Content)}
                                    </motion.div>
                                </AnimatePresence>

                            </motion.div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Step = ({ step, index, onActive }: { step: any, index: number, onActive: () => void }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { margin: "-50% 0px -50% 0px" });

    useEffect(() => {
        if (isInView) onActive();
    }, [isInView, onActive]);

    return (
        <div ref={ref} className="min-h-screen flex items-center py-20 relative">
            <div className="w-full max-w-lg"> {/* Constrain width for better readability */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    viewport={{ margin: "-20% 0px -20% 0px" }}
                >
                    {/* Clean Step Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <span className={`text-6xl font-bold font-mono tracking-tighter opacity-30 ${step.colors.text}`}>
                            {step.id}
                        </span>
                        <div className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
                        <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">
                            {step.category}
                        </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">
                        {step.title}
                    </h3>

                    {/* Subtitle */}
                    <p className={`text-xl font-medium mb-12 ${step.colors.text}`}>
                        {step.subtitle}
                    </p>

                    {/* Premium Feature Cards */}
                    <div className="space-y-4 mb-12">
                        {step.features.map((feature: any, i: number) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="group relative overflow-hidden"
                            >
                                <div className="relative flex items-center gap-5 p-5 rounded-2xl bg-zinc-900/40 border border-white/5 hover:bg-zinc-800/40 hover:border-white/10 transition-all duration-300">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border border-white/5 transition-colors duration-300 ${step.colors.iconBg} ${step.colors.text}`}>
                                        {feature.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-white font-semibold mb-1 group-hover:text-white transition-colors text-lg">
                                            {feature.title}
                                        </h4>
                                        <p className="text-zinc-500 text-sm leading-relaxed group-hover:text-zinc-400 transition-colors">
                                            {feature.desc}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile Visual */}
                    <div className="lg:hidden h-[50vh] min-h-[400px] w-full relative mb-12 bg-zinc-950 rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
                        {React.createElement(step.Content)}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default WorkflowSection;
